import React from 'react';
import { motion } from 'framer-motion';
import { Button, Chip } from '@heroui/react';
import { Clock, BookOpen, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Guide, GuideDifficulty } from '@/services/apis/guides';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/utils/cn';

interface GuideCardProps {
  guide: Guide;
  language?: string;
}

const GuideCard: React.FC<GuideCardProps> = ({ guide, language = 'en' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('guides');

  const translation = guide.translations?.find((t) => t.language === language) || guide.translations?.[0];
  const title = translation?.title || 'Untitled Guide';
  const description = translation?.description || '';

  const getDifficultyConfig = (difficulty: GuideDifficulty) => {
    switch (difficulty) {
      case GuideDifficulty.BEGINNER:
        return {
          color: 'from-emerald-500 to-teal-600',
          bg: 'bg-emerald-50 dark:bg-emerald-950/30',
          text: 'text-emerald-700 dark:text-emerald-300',
          border: 'border-emerald-200 dark:border-emerald-800',
          label: 'Beginner',
        };
      case GuideDifficulty.INTERMEDIATE:
        return {
          color: 'from-amber-500 to-orange-600',
          bg: 'bg-amber-50 dark:bg-amber-950/30',
          text: 'text-amber-700 dark:text-amber-300',
          border: 'border-amber-200 dark:border-amber-800',
          label: 'Intermediate',
        };
      case GuideDifficulty.ADVANCED:
        return {
          color: 'from-rose-500 to-pink-600',
          bg: 'bg-rose-50 dark:bg-rose-950/30',
          text: 'text-rose-700 dark:text-rose-300',
          border: 'border-rose-200 dark:border-rose-800',
          label: 'Advanced',
        };
      default:
        return {
          color: 'from-slate-500 to-gray-600',
          bg: 'bg-slate-50 dark:bg-slate-950/30',
          text: 'text-slate-700 dark:text-slate-300',
          border: 'border-slate-200 dark:border-slate-800',
          label: 'Unknown',
        };
    }
  };

  const handleViewGuide = () => {
    navigate(`/guides/${guide.id}`);
  };

  const stepCount = guide.steps?.length || 0;
  const estimatedTime = guide.estimatedTime ? `${guide.estimatedTime} min` : null;
  const difficultyConfig = getDifficultyConfig(guide.difficulty);

  return (
    <motion.div
      className="group relative bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200/60 dark:border-gray-800/60 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col h-full backdrop-blur-sm"
      whileHover={{ y: -8, scale: 1.02 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-blue-500/5 group-hover:via-purple-500/5 group-hover:to-pink-500/5 transition-all duration-500 pointer-events-none" />

      {/* Header with modern gradient */}
      <div className="relative overflow-hidden">
        <div className={cn('absolute inset-0 bg-gradient-to-br', difficultyConfig.color)} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent" />
        <div className="relative p-6 pb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0 pr-2">
              <h3
                title={title}
                className="text-2xl font-bold mb-3 line-clamp-2 leading-tight text-white drop-shadow-sm"
              >
                {title}
              </h3>
              {description && (
                <p title={description} className="text-white/85 text-sm line-clamp-2 leading-relaxed font-medium">
                  {description}
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Badges with modern styling */}
          <div className="flex flex-wrap gap-2">
            <Chip
              size="sm"
              variant="flat"
              className={cn(
                'border backdrop-blur-md bg-white/25 text-white font-semibold shadow-lg',
                difficultyConfig.border,
              )}
            >
              {difficultyConfig.label}
            </Chip>
            {guide.category && (
              <Chip
                size="sm"
                variant="flat"
                className="border backdrop-blur-md bg-white/25 text-white font-semibold shadow-lg border-white/30"
              >
                {typeof guide.category === 'object' ? guide.category.name : guide.category}
              </Chip>
            )}
            {guide.product && (
              <Chip
                size="sm"
                variant="flat"
                className="border backdrop-blur-md bg-white/25 text-white font-semibold shadow-lg border-white/30"
              >
                {typeof guide.product === 'object' ? guide.product.name : guide.product}
              </Chip>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col bg-gradient-to-b from-white to-gray-50/50 dark:from-gray-900 dark:to-gray-950/50">
        {/* Stats with modern icons */}
        <div className="flex items-center gap-6 mb-5 text-sm">
          {estimatedTime && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-950/50">
                <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="font-semibold">{estimatedTime}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-950/50">
              <BookOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="font-semibold">
              {stepCount} {stepCount === 1 ? t('card.step') : t('card.steps')}
            </span>
          </div>
        </div>

        {/* Steps Preview with modern design */}
        {guide.steps && guide.steps.length > 0 && (
          <div className="mb-6 flex-1">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4">
              {t('card.whatYoullLearn')}
            </h4>
            <div className="space-y-3">
              {guide.steps.slice(0, 3).map((step, index) => {
                const stepTranslation =
                  step.translations?.find((t) => t.language === language) || step.translations?.[0];
                const stepTitle = stepTranslation?.title || `Step ${index + 1}`;
                return (
                  <div key={step.id} className="flex items-start gap-3 group/step">
                    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md group-hover/step:scale-110 transition-transform">
                      <span className="text-xs text-white font-bold">{index + 1}</span>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 line-clamp-1 flex-1 font-medium pt-0.5">
                      {stepTitle}
                    </span>
                  </div>
                );
              })}
              {guide.steps.length > 3 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 ml-10 font-medium">
                  +{guide.steps.length - 3} {guide.steps.length - 3 === 1 ? t('card.moreStep') : t('card.moreSteps')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CTA Button with modern gradient */}
        <Button
          color="primary"
          variant="solid"
          className={cn(
            'w-full mt-auto h-12 font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300',
            'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600',
            'hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700',
            'text-white border-0',
          )}
          endContent={<ArrowRight className="w-5 h-5" />}
          onPress={handleViewGuide}
        >
          {t('card.viewGuide')}
        </Button>
      </div>
    </motion.div>
  );
};

export default GuideCard;
